// Pure helpers for AddMappingDialog (PRODOPSAI-510 / US7) and the inline
// "+ Create new role" mini-form shared with EditMembershipPopover
// (PRODOPSAI-521).
//
// Lives in its own module so:
//   1. SonarCloud S7721 (helpers defined inside component render) cannot fire.
//   2. US10 can unit-test them without rendering React.
//   3. The dialog component itself stays under SonarCloud's cognitive-complexity
//      threshold (S3776 < 15).

import type {
  IPersonListItem,
  ITeam,
  ITeamRole,
} from '@skystone/shared';

export const MAX_NOTE_LENGTH = 255;

export interface MappingPersonOption {
  value: string;
  label: string;
  email: string | null;
}

export interface MappingTeamOption {
  value: string;
  label: string;
  region: string;
}

export interface MappingRoleOption {
  value: string;
  label: string;
}

/**
 * Filter the People admin list to the Entra-linked, active, non-tombstoned
 * subset eligible for team mapping, and map to `{value,label}` rows sorted by
 * displayName. The dialog calls the existing `/admin/people` endpoint —
 * filtering happens client-side so no new backend route is needed (US7 spec).
 */
export function buildPersonOptions(items: readonly IPersonListItem[]): MappingPersonOption[] {
  const eligible = items.filter(personIsEligibleForMapping);
  const sorted = eligible.slice().sort((a, b) => a.displayName.localeCompare(b.displayName));
  return sorted.map((p) => ({
    value: p.id,
    label: p.entraDisplayName ?? p.displayName,
    email: p.primaryEmail,
  }));
}

function personIsEligibleForMapping(p: IPersonListItem): boolean {
  if (p.isActive === false) return false;
  if (p.isTombstoned === true) return false;
  if (p.linkedAccountCount <= 0) return false;
  return true;
}

/**
 * Build the team picker options from `GET /teams`. Archived teams are
 * dropped; the rest are returned sorted alphabetically with a region label
 * the dialog uses to group its `<optgroup>` headers.
 */
export function buildTeamOptions(teams: readonly ITeam[]): MappingTeamOption[] {
  const active = teams.filter((t) => t.archived === false);
  const sorted = active.slice().sort((a, b) => a.name.localeCompare(b.name));
  return sorted.map((t) => ({
    value: t.id,
    label: t.name,
    region: regionLabel(t.regionName),
  }));
}

function regionLabel(raw: string | null | undefined): string {
  if (raw === null || raw === undefined) return 'Unassigned region';
  const trimmed = raw.trim();
  if (trimmed === '') return 'Unassigned region';
  return trimmed;
}

/**
 * Group team options by region label for `<optgroup>` rendering. Regions are
 * returned alphabetical; the "Unassigned region" bucket lands last when
 * present.
 */
export function groupTeamsByRegionLabel(
  options: readonly MappingTeamOption[],
): Array<{ region: string; teams: MappingTeamOption[] }> {
  const byRegion = new Map<string, MappingTeamOption[]>();
  for (const opt of options) {
    const bucket = byRegion.get(opt.region);
    if (bucket === undefined) {
      byRegion.set(opt.region, [opt]);
    } else {
      bucket.push(opt);
    }
  }
  const names = Array.from(byRegion.keys());
  const UNASSIGNED = 'Unassigned region';
  const realRegions = names.filter((n) => n !== UNASSIGNED).sort((a, b) => a.localeCompare(b));
  const ordered = byRegion.has(UNASSIGNED) ? [...realRegions, UNASSIGNED] : realRegions;
  return ordered.map((region) => ({ region, teams: byRegion.get(region) ?? [] }));
}

/**
 * Filter the role catalog to non-archived rows, sorted by `sortOrder` then
 * displayName, and map to `{value,label}`. Archived roles are dropped so the
 * picker never offers a soft-deleted entry.
 */
export function buildRoleOptions(roles: readonly ITeamRole[]): MappingRoleOption[] {
  const active = roles.filter((r) => r.archived === false);
  const sorted = active.slice().sort((a, b) => {
    if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
    return a.displayName.localeCompare(b.displayName);
  });
  return sorted.map((r) => ({ value: r.id, label: r.displayName }));
}

/**
 * Convert the optional note input into the value the API expects: trimmed
 * non-empty string, or `null` for empty input. Whitespace-only is treated as
 * empty so the database never stores `'   '`.
 */
export function normaliseNote(input: string): string | null {
  const trimmed = input.trim();
  if (trimmed === '') return null;
  return trimmed;
}

/**
 * `true` iff every required field is set AND the note is within the length
 * cap. The dialog's Save button uses this to gate submission.
 *
 * PRODOPSAI-523: a binding is satisfied either by a real catalog role
 * (`teamRoleId !== ''`) OR by the "Same as my title" flag
 * (`useEntraTitle === true`).
 */
export function canSaveMapping(args: {
  personId: string;
  teamId: string;
  teamRoleId: string;
  useEntraTitle: boolean;
  noteLength: number;
}): boolean {
  if (args.personId === '') return false;
  if (args.teamId === '') return false;
  const hasRoleBinding = args.teamRoleId !== '' || args.useEntraTitle === true;
  if (hasRoleBinding === false) return false;
  if (args.noteLength > MAX_NOTE_LENGTH) return false;
  return true;
}

/**
 * PRODOPSAI-523: special-case option value for the "Same as my title" pinned
 * row at the top of the role picker. Never collides with a real UUID because
 * it starts with a sentinel prefix.
 */
export const SAME_AS_MY_TITLE_VALUE = '__same_as_my_title__';

/**
 * PRODOPSAI-523: build the wire body for a CREATE request out of the dialog's
 * form state. Returns either a catalog binding (`teamRoleId` set) or an
 * entra-title binding (`useEntraTitle: true`). Pure so the dialog stays under
 * SonarCloud's cognitive-complexity threshold.
 */
export function buildCreateBody(args: {
  personId: string;
  teamId: string;
  teamRoleId: string;
  useEntraTitle: boolean;
  note: string;
}): {
  personId: string;
  teamId: string;
  teamRoleId?: string;
  useEntraTitle?: boolean;
  note: string | null;
} {
  const note = normaliseNote(args.note);
  if (args.useEntraTitle === true) {
    return {
      personId: args.personId,
      teamId: args.teamId,
      useEntraTitle: true,
      note,
    };
  }
  return {
    personId: args.personId,
    teamId: args.teamId,
    teamRoleId: args.teamRoleId,
    note,
  };
}

/**
 * Detect HTTP 409 ConflictError from the api wrapper. The wrapper throws an
 * `ApiError` (unexported class) whose `.status` is the HTTP code; the dialog
 * surfaces 409 inline under the role field rather than as a toast.
 */
export function isConflictError(err: unknown): boolean {
  if (err === null || typeof err !== 'object') return false;
  const candidate = err as { status?: unknown };
  return candidate.status === 409;
}

/**
 * Detect HTTP 400 ValidationError from the api wrapper. The inline
 * "+ Create new role" mini-form surfaces backend validation errors next to
 * the input rather than as a toast.
 */
export function isValidationError(err: unknown): boolean {
  if (err === null || typeof err !== 'object') return false;
  const candidate = err as { status?: unknown };
  return candidate.status === 400;
}

/**
 * Convert a free-text display name into the snake_case `key` the backend
 * accepts (`/^[a-z][a-z0-9_]*$/`). Strips accents, collapses runs of
 * non-alphanumerics into a single `_`, and trims leading/trailing `_`s.
 * Returns `''` if the resulting key would not start with a letter — the
 * caller surfaces that as an inline validation error.
 */
export function deriveTeamRoleKey(displayName: string): string {
  const trimmed = displayName.trim().toLowerCase();
  if (trimmed === '') return '';
  // Strip diacritics (NFD then drop combining marks). Example: "Sénior" -> "senior".
  // `\p{M}` is the Unicode property escape for "Mark" — covers the Combining
  // Diacritical Marks block (U+0300..U+036F) and other combining classes without
  // an embedded literal range, which keeps SonarCloud's ReDoS scanner happy.
  const stripped = trimmed.normalize('NFD').replace(/\p{M}/gu, '');
  // Replace any run of non [a-z0-9] with a single underscore.
  const collapsed = stripped.replace(/[^a-z0-9]+/g, '_');
  // Trim leading and trailing underscores with two anchored replaces; alternation +
  // quantifiers tripped S5852 in iter 3.
  const trimmedUnderscores = collapsed.replace(/^_+/, '').replace(/_+$/, '');
  if (trimmedUnderscores === '') return '';
  // Must start with a letter. If it does not, the helper rejects the name —
  // the caller's mini-form treats `''` as "ask the user for a different name".
  if (/^[a-z]/.test(trimmedUnderscores) === false) return '';
  return trimmedUnderscores;
}

/**
 * Case-insensitive `displayName` match against the active role catalog —
 * powers the "A role named X already exists." soft-collision warning in the
 * inline create-role mini-form. Archived roles are ignored so the soft hint
 * never points at a soft-deleted entry.
 */
export function isLikelyDuplicateRoleName(
  name: string,
  existing: readonly ITeamRole[],
): ITeamRole | null {
  const target = name.trim().toLowerCase();
  if (target === '') return null;
  for (const role of existing) {
    if (role.archived === true) continue;
    if (role.displayName.trim().toLowerCase() === target) return role;
  }
  return null;
}
